import { Button, Flex } from '@chakra-ui/react';
import { IoArrowBack, IoArrowForward } from 'react-icons/io5';
import { getTranslation } from '../utils/translations';
import { useTranslation } from 'react-i18next';
import Field from '../components/Field';
import { useFormContext } from 'react-hook-form';
import { darkenHex, getReadableTextColor } from '../utils/colors';
import AfterSubmitPage from './AfterSubmitPage';
import StartPage from './StartPage';
import SubmissionSummaryPage from './SubmissionSummaryPage';
import { useEffect } from 'react';

export default function Page({ isSubmitting, survey, currentPage, setPage }: { isSubmitting: boolean; survey: Survey; currentPage: number; setPage: (page: number) => void }) {
	const { t } = useTranslation();
	const { trigger } = useFormContext();

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'instant' });
	}, [currentPage]);

	const surveyHasBeforeParagraph =
		getTranslation(survey.translations, 'surveys.beforeParagraph', 'fi', false) !== '' ||
		getTranslation(survey.translations, 'surveys.beforeParagraph', 'en', false) !== '' ||
		getTranslation(survey.translations, 'surveys.beforeParagraph', 'sv', false) !== '';

	const surveyHasTitle =
		getTranslation(survey.translations, 'surveys.title', 'fi', false) !== '' ||
		getTranslation(survey.translations, 'surveys.title', 'en', false) !== '' ||
		getTranslation(survey.translations, 'surveys.title', 'sv', false) !== '';

	const isStartPage = currentPage === -1;
	const hasStartPage = surveyHasBeforeParagraph && surveyHasTitle;

	const totalPages = (survey.surveyElements?.filter(el => el.elementType === 'pageBreak').length ?? 0) + 1;
	const isAfterSubmitPage = currentPage === totalPages + (survey.isSummaryPageShown ? 1 : 0);
	const isSummaryPage = currentPage === totalPages && survey.isSummaryPageShown;

	function getPageElements(): SurveyElement[] {
		const elements =
			survey.surveyElements?.sort((a, b) => {
				const aOrder = a.orderNumber ?? Infinity;
				const bOrder = b.orderNumber ?? Infinity;
				return aOrder - bOrder;
			}) ?? [];

		let page = 0;
		const result = [];

		for (const el of elements) {
			if (el.elementType === 'pageBreak') {
				page++;
				continue;
			}
			if (page === currentPage) {
				result.push(el);
			}
		}

		return result;
	}

	if (isStartPage) return <StartPage setPage={setPage} currentPage={currentPage} />;
	if (isSummaryPage) return <SubmissionSummaryPage isSubmitting={isSubmitting} setPage={setPage} currentPage={currentPage} />;
	if (isAfterSubmitPage) return <AfterSubmitPage />;

	return (
		<>
			<Flex flexDirection={'column'} gap={4} className="tav-survey-element-page">
				{getPageElements().map(element => (
					<Field key={element.surveyElementID} field={element} />
				))}
			</Flex>
			<Flex className="tav-survey-element-page-buttons" justifyContent="space-between" marginTop="auto" paddingTop={'20px'}>
				{hasStartPage || currentPage > 0 ? (
					<Button
						className="tav-survey-element-page-button-prev"
						_hover={{
							backgroundColor: darkenHex(survey.colorScheme, 20),
						}}
						backgroundColor={survey.colorScheme}
						onClick={() => setPage(currentPage - 1)}
						color={getReadableTextColor(survey.colorScheme)}
					>
						<IoArrowBack />
						{t('general.previous')}
					</Button>
				) : null}

				{currentPage === totalPages - (survey.isSummaryPageShown ? 0 : 1) ? (
					<Button
						marginLeft={'auto'}
						loading={isSubmitting}
						className="tav-survey-last-page-buttons-submit"
						backgroundColor={survey.colorScheme}
						_hover={{
							backgroundColor: darkenHex(survey.colorScheme, 20),
						}}
						type="submit"
						color={getReadableTextColor(survey.colorScheme)}
					>
						{t('general.submit')}
					</Button>
				) : (
					<Button
						className="tav-survey-element-page-button-next"
						backgroundColor={survey.colorScheme}
						_hover={{
							backgroundColor: darkenHex(survey.colorScheme, 20),
						}}
						color={getReadableTextColor(survey.colorScheme)}
						marginLeft={'auto'}
						onClick={async () => {
							const isHookFormValid = await trigger();
							const form = document.querySelector('form');
							const isNativeValid = form?.reportValidity();

							if (isHookFormValid && isNativeValid) {
								setTimeout(() => {
									setPage(currentPage + 1);
								}, 1);
							}
						}}
					>
						{t('general.next')}
						<IoArrowForward />
					</Button>
				)}
			</Flex>
		</>
	);
}
